import { camelCase, isEmpty, isNull, map } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import notFoundImage from '../../../common/assets/noImageAvailable.svg';
import { handleImageUrl } from '../../../common/helpers/sharpie/imageUrlHelper';
import Icon from '../../../common/styles/Icon';
import projectResource from '../../../locales/th/project.json';
import { WholeState } from '../../../store/common/type';
import { projectImageConfig } from '../../card/config/index';
import LazyImage from '../../lazyImage/LazyImage';
import {
  PropertyOptionAttribute,
  PropertyOptionAttributeData,
  PropertyOptionAttributeTitle,
  PropertyOptionAttributeWrapper,
  PropertyOptionConfig,
  PropertyOptionConfigWrapper,
  PropertyOptionContentWrapper,
  PropertyOptionImage,
  PropertyOptionInfo,
  PropertyOptionTitle,
  PropertyOptionWrapper,
  PropertyPrice,
  PropertyTitle,
  PropertyTitleWrapper,
} from './PropertyOption.style';

const PropertyOption: React.FC<PropertyOptionProps> = ({
  projectData,
  isProjectnetProject,
}) => {
  const deviceType = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.deviceType : 'desktop',
  );
  const isNextGenSupported = useSelector<WholeState, boolean>((state) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );

  const renderSharpieImage = (
    url: string,
    imgAlt: string,
    lazyLoad = true,
  ): React.ReactElement => {
    const imageInfo = projectImageConfig[deviceType];
    const sharpieImgUri = handleImageUrl(
      url,
      imageInfo.width,
      imageInfo.height,
      isNextGenSupported,
    );
    return <LazyImage src={sharpieImgUri} alt={imgAlt} lazyLoad={lazyLoad} />;
  };

  const formatPrice = (price: UnitPrice): string => {
    const currency = 'ล้านบาท';
    if (price.min !== 0 && price.max !== 0) {
      return `${price.min} - ${price.max} ${currency}`;
    }
    if (price.min !== 0 && price.max === 0) {
      return `From ${price.min} ${currency}`;
    }
    if (price.min === 0 && price.max !== 0) {
      return `To ${price.max} ${currency}`;
    }
    return 'No Information';
  };
  const renderPropertyOptionConfig = (item: Specification): React.ReactNode => {
    return (
      <PropertyOptionConfigWrapper>
        <PropertyOptionConfig>
          {item.bedrooms} <Icon iconName={'bed-small'} />
        </PropertyOptionConfig>
        <PropertyOptionConfig>
          {item.bathrooms} <Icon iconName={'bath-small'} />
        </PropertyOptionConfig>
        <PropertyOptionConfig data-test={'car-parking-spaces'}>
          {item.garage} <Icon iconName={'car-small'} />
        </PropertyOptionConfig>
      </PropertyOptionConfigWrapper>
    );
  };

  const renderPropertyOptionContent = (): React.ReactElement => {
    const titleText = projectResource.propertyOption.title;
    const categoryText = projectResource.propertyOption.category;
    const configurationText = projectResource.propertyOption.configuration;
    const sizeText = projectResource.propertyOption.size;

    return (
      <div>
        <PropertyOptionTitle>{titleText}</PropertyOptionTitle>
        {map(projectData, (item, i) => {
          const cardImage = !isNull(item.image.url) ? (
            renderSharpieImage(item.image.url, item.image.title)
          ) : (
            <div
              dangerouslySetInnerHTML={{ __html: notFoundImage }}
              className="no-image"
            />
          );

          return (
            <PropertyOptionContentWrapper key={i}>
              <PropertyOptionImage
                noImage={isEmpty(item.image.url)}
                data-test={'unit-type-option-img'}
              >
                {cardImage}
              </PropertyOptionImage>
              <PropertyOptionInfo>
                <PropertyTitleWrapper>
                  <PropertyTitle>{item.unit_name}</PropertyTitle>
                  <PropertyPrice>{formatPrice(item.price)}</PropertyPrice>
                </PropertyTitleWrapper>
                <PropertyOptionAttributeWrapper>
                  <PropertyOptionAttribute>
                    <PropertyOptionAttributeTitle>
                      {categoryText}:
                    </PropertyOptionAttributeTitle>
                    <PropertyOptionAttributeData
                      data-test={'unit-type-option-category'}
                    >
                      {isProjectnetProject
                        ? item.building_type.label
                        : (
                            projectResource.propertyOption as Record<
                              string,
                              string
                            >
                          )[camelCase(String(item.building_type.label))]}
                    </PropertyOptionAttributeData>
                  </PropertyOptionAttribute>
                  <PropertyOptionAttribute>
                    <PropertyOptionAttributeTitle>
                      {configurationText}:
                    </PropertyOptionAttributeTitle>
                    <PropertyOptionAttributeData>
                      {renderPropertyOptionConfig(item.specifications)}
                    </PropertyOptionAttributeData>
                  </PropertyOptionAttribute>
                  <PropertyOptionAttribute>
                    <PropertyOptionAttributeTitle>
                      {sizeText}:
                    </PropertyOptionAttributeTitle>
                    <PropertyOptionAttributeData>
                      {item.building_size} sq.m.
                    </PropertyOptionAttributeData>
                  </PropertyOptionAttribute>
                </PropertyOptionAttributeWrapper>
              </PropertyOptionInfo>
            </PropertyOptionContentWrapper>
          );
        })}
      </div>
    );
  };

  return (
    !isEmpty(projectData) && (
      <PropertyOptionWrapper>
        {renderPropertyOptionContent()}
      </PropertyOptionWrapper>
    )
  );
};

interface PropertyOptionProps {
  projectData: [ProjectUnits];
  isProjectnetProject?: boolean;
}

interface ProjectUnits {
  image: UnitImage;
  unit_name: string;
  price: UnitPrice;
  building_size: number;
  specifications: Specification;
  building_type: BuildingType;
}

interface BuildingType {
  label: number;
  value: number;
}

interface Specification {
  bedrooms: number;
  bathrooms: number;
  garage: number;
  number_of_floors?: number;
}

interface UnitImage {
  url: string;
  title: string;
  width: number;
  height: number;
}

interface UnitPrice {
  min: number;
  max: number;
}

export default PropertyOption;
